// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentQuestionEyebrow — section-label chrome for assessment questions
 * @version 0.2.0
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import styles from './AssessmentQuestionEyebrow.module.css';

/*
 * AssessmentQuestionEyebrow — section-label chrome for assessment QUESTION
 * screens. Pairs a section label with a position counter (current question
 * over total). 2-tier responsive primitive [sm · md].
 *
 *   md (≥ 768, wide container): horizontal — label · 32×1 teal rule · "Question N of M"
 *   md (≥ 768, narrow container): stacked — label / 32×1 teal rule / "Question N of M"
 *     (CSS container query — root is `container-type: inline-size`; an inner
 *     flex wrapper queries the root's width and flips `flex-direction` from
 *     `row` to `column` below the threshold. Used when the label is long
 *     enough that horizontal layout would wrap the label onto a second line.)
 *   sm (< 768): single full-width centered line — "LABEL N / M"
 *
 *   <AssessmentQuestionEyebrow
 *     label="Organizational Impact"
 *     questionNumber={1}
 *     totalQuestions={12}
 *   />
 *
 * Sibling to AssessmentEyebrow (which renders a single label above a
 * downward-pointing rule, for intro / section-divider / how-to-respond /
 * completion screens). This primitive is for the in-flow question screens
 * where the eyebrow carries both a section name AND the user's position.
 *
 * Detection: JS matchMedia (Pattern B) — the mobile layout composes the
 * label and counter into a single string and drops the rule, which @media
 * alone can't express cleanly. The horizontal-vs-stacked swap inside md
 * is pure CSS via a container query. Caller can pin a tier with
 * `forceLayout` for tests, popups, and visual-regression sidecars.
 */

export type AssessmentQuestionEyebrowLayout = 'sm' | 'md' | 'md-stacked';

export interface AssessmentQuestionEyebrowProps extends HTMLAttributes<HTMLDivElement> {
  /** Section / category label rendered on the left at md, combined with the counter at sm. Auto-uppercased via CSS. */
  label: string;
  /** Current question's position in the flow (1-indexed). */
  questionNumber: number;
  /** Total questions in the flow. */
  totalQuestions: number;
  /** Optional "skill" eyebrow, rendered below main eyebrow */
  skillSection?: string;
  /**
   * Pin the layout tier (skips matchMedia auto-detect + container query).
   *   - `sm` = single-line combined layout (viewport < 768)
   *   - `md` = horizontal three-part layout (viewport ≥ 768, wide container)
   *   - `md-stacked` = vertical three-stack layout (viewport ≥ 768, narrow container or long label)
   */
  forceLayout?: AssessmentQuestionEyebrowLayout;
  /**
   * Colour treatment. `dark` (default) for dark surfaces; `light` is the
   * navy-ish label + teal-accessible rule for the light-surface flow. Shares
   * the light values with AssessmentEyebrow. A per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentQuestionEyebrow = forwardRef<HTMLDivElement, AssessmentQuestionEyebrowProps>(
  function AssessmentQuestionEyebrow(
    {
      label,
      questionNumber,
      totalQuestions,
      skillSection,
      forceLayout,
      className,
      theme = 'dark',
      as,
      ...rest
    },
    ref,
  ) {
  const Component = as ?? 'div';
  const [isBelow, setIsBelow] = React.useState<boolean>(forceLayout === 'sm');

  React.useEffect(() => {
    if (forceLayout) {
      setIsBelow(forceLayout === 'sm');
      return;
    }
    if (typeof window === 'undefined') return;
    // Figma source-of-truth: eyebrow flips to mobile variant below 768.
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsBelow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [forceLayout]);

  const rootClass = [styles.root, theme === 'light' ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  if (isBelow) {
    return (
      <Component ref={ref} className={rootClass} data-layout="sm" {...rest}>
        <span className={styles.combinedText}>
          {label} {questionNumber} / {totalQuestions}
        </span>
        {skillSection &&
          <p className={styles['label-wrap']}>{skillSection}</p>
        }
      </Component>
    );
  }

  const mdLayout = forceLayout === 'md-stacked' ? 'md-stacked' : 'md';

  return (
    <Component ref={ref} className={rootClass} data-layout={mdLayout} {...rest}>
      <div className={styles.inner}>
        <span className={styles.label}>{label}</span>
        <span className={styles.rule} aria-hidden="true" />
        {skillSection && (
          <>
            <span className={styles.label}>{skillSection}</span>
            <span className={styles.rule} aria-hidden="true" />
          </>
        )}
        <span className={styles.count}>
          Question {questionNumber} of {totalQuestions}
        </span>
      </div>
    </Component>
  );
  },
);

AssessmentQuestionEyebrow.displayName = 'AssessmentQuestionEyebrow';
